import React, { useState } from 'react';
import styled from 'styled-components';
import { MENU_ITEM_TYPE } from '@typings/menu/menuItemType';
import { MENU_TEXT_TYPE, MENU_TEXT_TYPE_VALUE } from '@typings/menu/menuTextType';
import NavItem from './NavItem';
import { TOOLTIP_DIR_TYPE_VALUE } from '@typings/common/tooltipDirType';
import { motion } from 'framer-motion';
import DropdownWrapper, { dirType } from '@components/wrapper/DropdownWrapper';
import { IBoardSet } from '@typings/menu/boardSetType';
import Tooltip from '@components/dataDisplay/Tooltip';
import { useReadPermission } from '@hooks/usePermission';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import { imageAlignmentStyles, NavIcon } from './commonStyle';
import useWindowWidth from '@hooks/useWindowWidth';

const menuAlignMap: Record<string, string> = {
  [MENU_TEXT_TYPE.LEFT]: 'flex-start',
  [MENU_TEXT_TYPE.CENTER]: 'center',
  [MENU_TEXT_TYPE.RIGHT]: 'flex-end',
};

/** 버튼 + 드롭다운을 하나의 gap 단위로 묶는 컨테이너 */
const FolderContainer = styled.div<{
  $menuTextType: MENU_TEXT_TYPE_VALUE;
  $isOpen: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isOpen }) => ($isOpen ? '12px' : '0')};
  align-items: ${({ $menuTextType }) => menuAlignMap[$menuTextType] || 'flex-start'};
  width: 100%;
`;

const DropMenuWrap = styled(motion.section)<{ $align?: string }>`
  display: flex !important;
  flex-direction: column;
  gap: var(--menuGap, 8px);
  align-items: ${({ $align }) => $align || 'flex-start'};
`;

export const topToBottomAni = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1],
    },
    display: 'block',
    transform: 'scaleY(1)',
    transformOrigin: 'top left',
    height: 0,
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.755, 0.05, 0.855, 0.06],
    },
    transitionEnd: {
      display: 'none',
      // overflow: 'auto',
    },
    transform: 'scaleY(0.8)',
    transformOrigin: 'top left',
    height: 'auto',
  },
};

const NavItemBtn = styled.button`
  line-height: 1;
  padding: 8px 12px;
  min-height: 30px;
  width: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  border-radius: var(--buttonBorderRadius);
  transition: all 0.2s;
  color: var(--menuTextColor);
  font-family: var(--menuFontFamily);
  font-size: 14px;

  &:hover {
    color: var(--primaryColor);
    cursor: var(--hoverCursorURL), pointer;
  }
`;

type Props = {
  data: any;
  subMenuList: IBoardSet[];
  menuItemType: any;
  layoutType: LAYOUT_TYPE_VALUE;
  menuTextType: MENU_TEXT_TYPE_VALUE;
  tooltipDir?: TOOLTIP_DIR_TYPE_VALUE;
  dropdownDirection?: keyof typeof dirType | keyof typeof import('@components/wrapper/DropdownWrapper').dropdownDirMap;
  noChangeMobile?: boolean;
};

function NavFolder({
  data,
  subMenuList,
  menuItemType,
  menuTextType,
  layoutType,
  tooltipDir,
  dropdownDirection,
  noChangeMobile,
}: Props) {
  const readPermission = useReadPermission({
    accessType: data.accessType,
    authorUid: '',
  });
  const ww = useWindowWidth();
  const isMobile = (ww || 0) <= 480;
  const { title, imgURL } = data;
  const hoverImgURL = (data as any)?.hoverImgURL || '';
  const mobileImgURL = (data as any)?.mobileImgURL || '';
  const currentImg = noChangeMobile ? imgURL : (isMobile && mobileImgURL ? mobileImgURL : imgURL) || imgURL;
  const hasHover = !!hoverImgURL && !isMobile;
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const handleToggleOpenMenu = (e) => {
    e.stopPropagation();
    setOpenSubMenu(!openSubMenu);
  };

  const effectiveMenuItemType =
    menuItemType === MENU_ITEM_TYPE.IMAGE && !currentImg
      ? MENU_ITEM_TYPE.TEXT
      : menuItemType;

  const drawMap = {
    [MENU_ITEM_TYPE.IMAGE]: (
      <Tooltip message={title} direction={tooltipDir || 'right'} isPortal hidden={isMobile}>
        <NavIcon
          style={{ ...imageAlignmentStyles[menuTextType], width: 'auto', display: 'inline-flex' }}
          $hasHover={hasHover}
        >
          <img className="base-img" src={currentImg} alt={title} loading="lazy" />
          {hasHover && (
            <img className="hover-img" src={hoverImgURL} alt={`${title}-hover`} loading="lazy" />
          )}
        </NavIcon>
      </Tooltip>
    ),
    [MENU_ITEM_TYPE.TEXT]: (
      <NavItemBtn style={{ textAlign: menuTextType }}>{title}</NavItemBtn>
    ),
  };

  const menuList = subMenuList.map((list, i) => {
    return (
      <NavItem
        key={list.id}
        list={list}
        tooltipDir={tooltipDir}
        layoutType={LAYOUT_TYPE.TOP}
        menuTextType={menuTextType}
        menuItemType={menuItemType}
        noChangeMobile={noChangeMobile}
      />
    );
  });

  if (!readPermission) return null;

  if (layoutType === LAYOUT_TYPE.TOP)
    return (
      <div
        style={{ position: 'relative' }}
        onClick={handleToggleOpenMenu}
        data-sound-type="menu"
      >
        {drawMap[effectiveMenuItemType]}
        {openSubMenu && (
          <DropdownWrapper
            direction="topLeft"
            mobileFilled
            style={{ textAlign: menuTextType, gap: 'var(--menuGap, 8px)' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>{menuList}</div>
          </DropdownWrapper>
        )}
      </div>
    );

  return (
    <FolderContainer $menuTextType={menuTextType} $isOpen={openSubMenu}>
      <motion.div
        data-sound-type="menu"
        onClick={handleToggleOpenMenu}
        style={{ cursor: 'pointer', display: 'inline-flex', textAlign: menuTextType }}
      >
        {drawMap[effectiveMenuItemType]}
      </motion.div>
      <DropMenuWrap
        as={motion.div}
        key={data.id || 'folder'}
        $align={menuAlignMap[menuTextType] || 'flex-start'}
        initial={false}
        animate={openSubMenu ? 'open' : 'collapsed'}
        variants={{
          open: { opacity: 1, height: 'auto', pointerEvents: 'auto' },
          collapsed: {
            opacity: 0,
            height: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          },
        }}
        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      >
        {menuList}
      </DropMenuWrap>
    </FolderContainer>
  );
}

export default React.memo(NavFolder);
